<template>
  <div class="wrapperEditCutPanel">
    <!-- cut form -->
    <cut-video-form
      v-if="this.videoInfo && !this.videoInfo.error && selectedSourceVideoId != 0"
      v-model="ranges"
      class="cut-modal__form"
      :video-info="videoInfo"
      :ranges="ranges"
      :duration="flooredDuration"
      :disabled="isLoading"
      :transition-type.sync="transitionType"
      :mode-select="mode"
      :selected-source-video-id="selectedSourceVideoId"
      @mode="modeSelect = $event"
      @preview="startPreview"
      @changeMode="$emit('selectMode', $event)"
      @stopPreview="stopPreview"
    />
    <div v-if="this.videoInfo && !this.videoInfo.error" class="row">
      <template>
        <div class="button-container">
          <el-button
            v-loading="isLoading"
            class="button-container__btn fontSanSerif"
            type="success"
            title="Cut selected intervals as new clips"
            @click="startCut(false)"
          >
            Crop
          </el-button>
        </div>
        <div class="button-container">
          <el-button
            v-loading="isLoading"
            :disabled="inverseRanges.length == 0"
            class="button-container__btn fontSanSerif"
            type="success"
            title="Cut selected intervals and concat rest as new clip"
            @click="startCut(true)"
          >
            Inverse Crop
          </el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CutVideoForm from "@/components/media/editing/CutVideoForm.vue";
import { DISCRETE } from "./Transition_types";
import { MODES_DRAG, MODES_PREVIEW } from "./cut_modes";
import { areTimesEqual, roundToMilliseconds } from "@/utils/time.js";

// @group Dialog
// Modal form to cut ranges from video clip
export default {
  name: "EditCutPanel",
  components: {
    CutVideoForm,
  },
  props: {
    selectedSourceVideoId: {
      type: Number,
      default: -1,
    },
    /** is form opened */
    visible: {
      type: Boolean,
      default: true,
    },
    /** video source link */
    videoSrc: {
      type: [Object, String],
      required: true,
    },
    /** video duration */
    videoDuration: {
      type: Number,
      default: 1,
    },
    videoInfo: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  /** VideoJS player options */
  playerOptions: {
    /** Autoplay need to initialize player area at start,
     * otherwise crop/text/image areas will can go beyound
     * player boundary */
    autoplay: true,
    controls: true,
    fill: true,
    preload: "none",
    cors: true,
    children: {
      controlBar: {
        captionsButton: false,
        chaptersButton: false,
        fullscreenToggle: false,
        subtitlesButton: false,
        playbackRateMenuButton: false,
        pictureInPictureToggle: false,
      },
    },
  },
  data() {
    return {
      isLoading: false,
      ranges: [[0, roundToMilliseconds(this.videoDuration)]],
      playerPaused: true,
      playerTime: 0,
      renderPlayer: false,
      unwatchPlayerTime: null,
      transitionType: DISCRETE,
      startTime: 0,
      endTime: 100,
      isPlayingPreview: false,
      modeSelect: MODES_DRAG,
    };
  },
  computed: {
    // @vuese
    // Duration that rounded down to make it integer
    flooredDuration() {
      return roundToMilliseconds(this.videoDuration);
    },
    // @vuese
    // Return array of inverse ranges (not selected areas) to made inverse cut
    inverseRanges() {
      const inverse = [];

      let lastEnd = 0;
      for (const [start, end] of this.ranges) {
        if (lastEnd !== start) {
          inverse.push([lastEnd, start]);
        }

        lastEnd = end;
      }

      if (lastEnd < this.flooredDuration) {
        inverse.push([lastEnd, this.flooredDuration]);
      }

      return inverse;
    },
  },
  watch: {
    videoDuration() {
      this.ranges = [[0, roundToMilliseconds(this.videoDuration)]];
    },
    ranges(newRanges, oldRanges) {
      if (newRanges.length !== oldRanges.length) {
        /** it's only add/delete range, no need to sync player */
        return;
      } else {
        /** sync player with changed time */
        for (let i = 0; i < newRanges.length; i++) {
          for (let j = 0; j < 2; j++) {
            const oldPointer = oldRanges[i][j];
            const newPointer = newRanges[i][j];
            if (!areTimesEqual(oldPointer, newPointer)) {
              this.playerTime = newPointer;
              this.playerPaused = true;
            }
          }
        }
      }
    },
    mode() {
      this.$emit("mode", this.mode);
    },
    playerPaused() {
      this.$emit("playerPaused", this.playerPaused);
    },
    playerTime(){
      this.$emit("playerTime", this.playerTime);
    },
  },
  /*
  updated() {
    this.$emit("playerTime", this.playerTime);
  },
  */
  methods: {
    open() {
      this.renderPlayer = true;
    },
    handleOpened() {
      this.syncPlayerTime();
      this.unwatchPlayerTime = this.$watch("playerTime", (newTime, oldTime) => {
        if (Math.abs(newTime - oldTime) > 0.5) {
          this.syncPlayerTime();
        }
      });
    },
    // @vuese
    // Close modal
    close() {
      this.$emit("update:visible", false);
      // Modal closed by user
      this.$emit("closed");
      this.unwatchPlayerTime();
      if (this.mode === MODES_PREVIEW) {
        this.stopPreview();
        this.$emit("stopPreview", true);
      }
    },
    // @vuese
    // Start cutting proccess
    // @arg inverse - is crop inverse or normal
    startCut(inverse) {
      if (this.isLoading) return;
      this.isLoading = false; // ЗАГЛУШКА НА ЗАГРУЖЕНО ИЛИ НЕТ
      // this.isLoading = true;

      let ranges = null;
      let done = null;
      let sourceId = null;
      let thumbnailLink = null;
      let title = null;

      if (!inverse) {
        sourceId = this.videoInfo.id;
        ranges = this.ranges;
        done = this.finishCut;
        thumbnailLink = this.videoInfo.thumbnailLink;
        title = this.videoInfo.title;
      } else {
        sourceId = this.videoInfo.id;
        thumbnailLink = this.videoInfo.thumbnailLink;
        ranges = this.inverseRanges;
        title = this.videoInfo.title;
        done = () => {
          this.ranges = [[0, this.flooredDuration]];
          this.isLoading = false;
          this.close();
        };
      }

      this.$emit("cut", {
        inverse,
        ranges,
        sourceId,
        thumbnailLink,
        title,
        done,
        transitionType: this.transitionType,
        failed: () => {
          this.isLoading = false; // ЗАГЛУШКА НА ЗАГРУЖЕНО ИЛИ НЕТ
        },
      });
    },
    // @vuese
    // Handle cut process finishing
    // @arg successes - list of boolean success flags for every range
    finishCut(successes) {
      const restRanges = [];

      for (let i = 0; i < this.ranges.length; i++) {
        if (!successes[i]) {
          restRanges.push(this.ranges[i]);
        }
      }

      /** if all ranges cut successfully -> close modal & add default range */
      if (restRanges.length === 0) {
        this.close();
        this.ranges = [[0, this.flooredDuration]];
      } else {
        this.ranges = restRanges;
      }

      this.isLoading = false;
    },
    syncPlayerTime() {
      this.$refs.cutPlayer.setTime(this.playerTime);
    },
    startPreview([start, end]) {
      this.$emit("startPreview", [start, end]);
    },
    stopPreview() {
      this.$emit("stopPreview", true);
    },
  },
};
</script>

<style lang="scss">
.wrapperEditCutPanel {
  width: 100%;
}
.row {
  display: flex;
  justify-content: center;
  flex-wrap: inherit; // wrap buttons on tight screens.. (Now I'm changing it to the inherit because the removal of the rest of the)
}

.button-container {
  margin-right: 0.5em;
  margin-left: 0.5em;
  margin-bottom: 0.5em;

  &__btn {
    width: 13em;
  }
}

.fontSanSerif {
  // font-family: sans-serif;
}

/** sized styles */
.el-dialog.cut-modal {
  width: 800px;
  margin-top: 40px !important;
  margin-bottom: 40px !important;
}

.cut-modal {
  &__player {
    height: 400px;
  }

  &__form {
    // margin-top: 2em;
    // height: 100%;
  }
}

@media only screen and (max-width: 879px), only screen and (max-height: 899px) {
  .el-dialog.cut-modal {
    width: 600px;
    margin-top: 20px !important;
    margin-bottom: 20px !important;
  }

  .cut-modal {
    &__player {
      height: 300px;
    }
  }
}

@media only screen and (max-width: 639px), only screen and (max-height: 740px) {
  .el-dialog.cut-modal {
    width: 380px;
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .cut-modal {
    &__player {
      height: 200px;
    }
  }

  /** tabs styles */
  .tabs-row {
    &__tab {
      .material-design-icon .material-design-icon__svg {
        height: 1.5rem !important;
        width: 1.5rem !important;
      }
    }
  }
}

@media only screen and (max-width: 399px) {
  .el-dialog.cut-modal {
    width: calc(100% - 30px);
    min-width: 190px;
    margin-top: 5px !important;
    margin-bottom: 5px !important;
  }

  .cut-modal {
    &__player {
      height: auto;
    }
  }
}
</style>
